import React from 'react';
import styled from 'styled-components';
import { compose } from 'redux';
import { useDispatch, useSelector } from 'react-redux';

import { v4 as uuidv4 } from 'uuid';
import { SAVES_TYPES } from 'right-revenue-microservice-sdk/lib/constants';
import session from '../../../App/session';
import injectSaga from '../../../../utils/redux/injectSaga';
import injectReducer from '../../../../utils/redux/injectReducer';

import { DOMAIN } from '../../constants';
import reducer from '../../reducer';
import saga from '../../saga';

import * as actions from '../../actions';
import { getCSVData } from './utils';
import { Affix } from 'antd';

import Toggle from 'react-toggle';
import Proposals from './Proposals';
import Inclusions from './Inclusions';
import IncrementsTable from './IncrementsTable';
import TotalRow from './TotalRow';
import FormActions from '../common/FormActions';

import 'react-toggle/style.css';
import './rateCalculator.style.css';
import getSymbolFromCurrency from 'currency-symbol-map';

const Container = styled.div`
    padding: 2em;
`;

const HeadPanel = styled.div`
    left: 1rem;
    right: 1rem;
    padding: 1rem;
    background-color: white;
    border: 0.1em solid #eaeaea;
    box-shadow: 0 0.2em 0.4em 0 rgba(215, 215, 215, 0.5);
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    width: auto;
`;

const Panel = styled.div`
    padding: 1.5em;
    margin-bottom: 9em;
    margin-top: 1em;
    background-color: white;
    border: 0.1em solid #eaeaea;
    box-shadow: 0 0.2em 0.4em 0 rgba(215, 215, 215, 0.5);
    overflow-y: hidden;
`;

const Title = styled.h2`
    font-family: ${({ theme }) => theme.font.title};
    font-size: 1.5625rem;
    color: ${({ theme }) => theme.colour.dark};
    text-align: left;
    margin-top: 0.5em;
    margin-bottom: 0.5em;
`;

const RateCalculatorForm = () => {
    const dispatch = useDispatch();

    const removeItem = (id) => {
        dispatch(actions.updatePackageCalculatorInclusions(id, 'DELETE'));
    };

    const onDateInputChange = (fieldName, value) => {
        dispatch(actions.updatePackageCalculatorProposals(fieldName, value));
    };

    const onInputChange = (event) => {
        const { name, value } = event.target;
        dispatch(actions.updatePackageCalculatorProposals(name, value));
    };

    const onInclusionsInputChange = (id, value) => {
        dispatch(actions.updatePackageCalculatorInclusions(id, 'VALUE', value));
    };

    const onInclusionsNameChange = (id, value) => {
        dispatch(actions.updatePackageCalculatorInclusions(id, 'NAME', value));
    };

    const onSelectChange = (event, meta) => {
        const { value } = event;
        const { name } = meta;
        dispatch(actions.updatePackageCalculatorProposals(name, value));
    };

    const onInclusionsSelectChange = (id, value) => {
        dispatch(actions.updatePackageCalculatorInclusions(id, 'PRICEPER', value));
    };

    const onToggleChange = (id, value) => {
        dispatch(actions.updatePackageCalculatorInclusions(id, 'TOGGLE', value));
    };

    const onAddNewInclusionRow = () => {
        dispatch(actions.updatePackageCalculatorInclusions(uuidv4(), 'CREATE'));
    };

    const onChangeIncrementsField = (fieldName, value) => {
        dispatch(actions.updatePackageCalculatorIncrements(fieldName, value));
    };

    const currency = useSelector(session.selectors.selectCurrency);

    const formData = useSelector((state) => state.formData.packageCalculator);

    const { netProfit } = formData;
    const csvData = getCSVData(formData);
    const currencyIcon = getSymbolFromCurrency(currency);
    const selectStyles = {
        option: (provided, state) => ({
            ...provided,
            color: state.isSelected ? 'white' : 'black',
            backgroundColor: state.isSelected ? '#125651' : 'white',
        }),
        control: (provided, state) => ({
            ...provided,
            border: state.isFocused ? '0.1em solid #30b79c' : '0.1em solid #D3D3D3',
            boxShadow: state.isFocused ? '0 0 0.5em rgba(48, 181, 155, 0.6)' : 'none',
            '&:hover': {
                border: '0.1em solid #30B79C',
            },
        }),
    };

    const profitValue = `${currencyIcon}${!Number.isNaN(netProfit) ? netProfit.toFixed(2) : (0).toFixed(2)}`;

    return (
        <Container className="pkgCalc-main">
            <Affix offsetTop={80}>
                <HeadPanel>
                    <Title>Profit Predictor</Title>
                    <Title>{`Net Profit: ${profitValue}`}</Title>
                </HeadPanel>
            </Affix>
            <Panel>
                <Proposals
                    formData={formData}
                    currency={currencyIcon}
                    onDateInputChange={onDateInputChange}
                    onInputChange={onInputChange}
                    onSelectChange={onSelectChange}
                    selectStyles={selectStyles}
                />
                <Inclusions
                    formData={formData}
                    currency={currencyIcon}
                    removeItem={removeItem}
                    onToggleChange={onToggleChange}
                    onInputChange={onInclusionsInputChange}
                    onSelectChange={onInclusionsSelectChange}
                    onAddRow={onAddNewInclusionRow}
                    onInputNameChange={onInclusionsNameChange}
                    selectStyles={selectStyles}
                />
                <TotalRow
                    formData={formData}
                    netProfit={netProfit}
                    currency={currencyIcon}
                    onInputChange={onInputChange}
                />
                <h3 style={{ fontSize: '1.5em' }}>Would you like to apply increments?</h3>
                <Toggle
                    checked={formData.applyIncrements}
                    onChange={(event) => onChangeIncrementsField('applyIncrements', event.target.checked)}
                    className="pkgCalc-form-toggle"
                />
                {formData.applyIncrements && (
                    <IncrementsTable
                        onChangeFieldValue={onChangeIncrementsField}
                        formData={formData}
                        currency={currencyIcon}
                        selectStyles={selectStyles}
                    />
                )}
            </Panel>
            <FormActions
                formType={SAVES_TYPES.PROFIT_PREDICTOR}
                extractStateToSave={(state) => ({
                    packageCalculator: state.packageCalculator,
                })}
                exportedCSVData={csvData}
                exportedFileName="right-revenue-profit-predictor.csv"
            />
        </Container>
    );
};

const withReducer = injectReducer({ key: DOMAIN, reducer });
const withSaga = injectSaga({ key: DOMAIN, saga });

export default compose(withReducer, withSaga)(RateCalculatorForm);
